import {
  Button,
  Heading,
  Icon,
  Modal,
  Text,
  ButtonProps,
} from "@ttc3k/trekker";
import { Box, Divider, Flex, Grid, styled } from "styled-system/jsx";
import {
  ForwardRefExoticComponent,
  PropsWithChildren,
  RefAttributes,
  SVGProps,
  useLayoutEffect,
  useRef,
} from "react";
import { t } from "i18next";
import { Xmark } from "iconoir-react";
import { css } from "styled-system/css";

export type WhereaboutsModalProps = PropsWithChildren & {
  isOpen: boolean;
  onClose: () => void;
  hideCloseTrigger?: boolean;
  HeaderIcon?: ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
  >;
  title?: string;
  description?: string;
  saveButtonProps?: ButtonProps;
  saveButtonCopy?: string;
  cancelButtonProps?: ButtonProps;
  cancelButtonCopy?: string;
  size?: "sm" | "md" | "lg";
  customFooter?: JSX.Element;
  disableBodyPadding?: boolean;
  portal?: boolean;
  isMaxZIndex?: boolean;
  isOverflowVisible?: boolean;
  initialFocus?: null | false | (() => HTMLElement | null);
  /**
   * Compact centered dialogs: one white surface (no section rules), title row + copy +
   * large Trekker-style actions right-aligned in the content flow (no separate footer bar).
   */
  compact?: boolean;
};

const ModalContent = styled(Grid, {
  base: {
    w: "full",
    bg: "bg.wa.light",
    h: "auto",
    maxH: "90vh",
    minH: "300px",
    overflow: "hidden",
    gridTemplateRows: {
      base: "auto minmax(0, 1fr) 1px auto",
      md: "auto minmax(0, 1fr) 1px auto",
    },
    gridRowGap: 0,
    gridGap: 0,
    gap: 0,
  },
  variants: {
    overflowVisible: {
      true: {
        overflow: "visible",
      },
    },
    size: {
      sm: {
        w: {
          base: "full",
          md: "400px",
        },
      },
      md: {
        w: {
          base: "full",
          md: "600px",
        },
      },
      lg: {
        w: {
          base: "full",
          md: "800px",
          lg: "1000px",
        },
      },
    },
  },
  defaultVariants: {
    size: "md",
  },
});

/** Compact: single auto row, content-height shell, white panel. */
const modalCompactShellClass = css({
  h: "fit-content!",
  minH: "min-content!",
  gridTemplateRows: "auto!",
  bg: "white!",
});

export const WhereaboutsModal = ({
  isOpen,
  onClose,
  hideCloseTrigger,
  HeaderIcon,
  title,
  description,
  saveButtonProps,
  saveButtonCopy,
  cancelButtonProps,
  cancelButtonCopy,
  size = "md",
  customFooter,
  disableBodyPadding,
  children,
  portal = true,
  isMaxZIndex = false,
  isOverflowVisible = false,
  initialFocus,
  compact = false,
}: WhereaboutsModalProps) => {
  const modalContentRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const content = modalContentRef.current;
    const scrollEl = content?.parentElement;
    if (scrollEl?.id !== "trekker-scroll-container") return;

    const prev = {
      height: scrollEl.style.height,
      minHeight: scrollEl.style.minHeight,
      overflow: scrollEl.style.overflow,
    };
    scrollEl.style.height = "auto";
    scrollEl.style.minHeight = "0";
    scrollEl.style.overflow = "hidden";

    return () => {
      scrollEl.style.height = prev.height;
      scrollEl.style.minHeight = prev.minHeight;
      scrollEl.style.overflow = prev.overflow;
    };
  }, [isOpen]);

  const descriptionInHeader = Boolean(description) && !compact;
  const descriptionInBody = Boolean(description) && compact;

  const bodyPadding = {
    base: disableBodyPadding ? 0 : "300",
    md: disableBodyPadding ? 0 : "500",
  } as const;

  const compactPadding = disableBodyPadding
    ? { base: "300", md: "300" }
    : { base: "300", md: "500" };

  const defaultButtons = !customFooter && (
    <Flex justifyContent="flex-end" gap="150" flexWrap="wrap">
      <Button
        size="lg"
        type="button"
        visual="primary"
        mode="light"
        {...cancelButtonProps}
      >
        {cancelButtonCopy ?? t("core:CANCEL")}
      </Button>
      <Button size="lg" type="button" {...saveButtonProps}>
        {saveButtonCopy ?? t("core:MODAL_BUTTON_COPY.SAVE")}
      </Button>
    </Flex>
  );

  return (
    <Modal
      open={isOpen}
      placement={"centerPane"}
      onOpenChange={({ open }: { open: boolean }) => {
        if (!open) onClose();
      }}
      hideCloseTrigger={compact || hideCloseTrigger}
      initialFocus={initialFocus}
      closeTriggerProps={{
        visual: "subtle",
        className: css({
          top: "200!",
          right: "28px!",
          borderRadius: "full",
          _focusVisible: {
            outline: "1px solid",
            outlineColor: "border.light",
            outlineOffset: "0px",
          },
          zIndex: 1,
        }),
        children: <Icon Element={Xmark} size={"lg"} />,
      }}
      noMaxH
      portal={portal}
      userSelect="none"
      zIndex={isMaxZIndex ? "max" : "medium"}
    >
      <ModalContent
        ref={modalContentRef}
        size={size}
        overflowVisible={isOverflowVisible}
        className={compact ? modalCompactShellClass : undefined}
        userSelect={"none"}
      >
        {compact ? (
          <Flex
            flexDir="column"
            bg="white"
            w="full"
            p={compactPadding}
            gap="400"
            maxH={{ base: "unset", md: "90vh" }}
            overflowY={isOverflowVisible ? "visible" : "auto"}
            overflowX="hidden"
            id="whereabouts-modal-content-wrapper"
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
              gap="200"
              w="full"
              minW={0}
            >
              <Flex alignItems="center" gap="150" minW={0} flex={1}>
                {HeaderIcon && <Icon Element={HeaderIcon} />}
                <Heading size="h5" color="text.dark">
                  {title}
                </Heading>
              </Flex>
              <styled.button
                type="button"
                onClick={onClose}
                aria-label={t("core:MODAL_BUTTON_COPY.CLOSE")}
                flexShrink={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                p="100"
                mr="-100"
                borderRadius="full"
                cursor="pointer"
                color="text.mid"
                transitionDuration="normal"
                transitionProperty="color, background"
                _hover={{ color: "text.dark", bg: "bg.wa.lightest" }}
                _focusVisible={{
                  outline: "1px solid",
                  outlineColor: "border.light",
                  outlineOffset: "1px",
                }}
              >
                <Icon Element={Xmark} size="lg" />
              </styled.button>
            </Flex>

            {descriptionInBody && (
              <Text color="text.mid">{description}</Text>
            )}

            {children}

            {customFooter ?? defaultButtons}
          </Flex>
        ) : (
          <>
            {/* Header */}
            <Flex
              bg={"white"}
              py="300"
              px="36px"
              flexDir={"column"}
              gap={"200"}
              borderBottomWidth={"1px"}
              borderBottomColor={"border.light"}
              borderBottomStyle={"solid"}
            >
              <Flex alignItems={"center"} gap={"150"}>
                {HeaderIcon && <Icon Element={HeaderIcon} />}
                <Heading size={"h5"} color={"text.dark"}>
                  {title}
                </Heading>
              </Flex>

              {descriptionInHeader && (
                <Text color={"text.mid"}>{description}</Text>
              )}
            </Flex>

            {/* Body */}
            <Box
              minH={0}
              maxH={{
                base: "unset",
                md: "59vh",
              }}
              p={bodyPadding}
              overflowY={isOverflowVisible ? "visible" : "auto"}
              overflowX={isOverflowVisible ? "visible" : "hidden"}
              overscrollBehavior="contain"
              id={"whereabouts-modal-content-wrapper"}
            >
              {children}
            </Box>

            {/* Footer */}
            <Divider borderColor={"border.light"} />
            {customFooter ? (
              customFooter
            ) : (
              <Flex
                px={{ base: "300", md: "500" }}
                py={{ base: "300", md: "400" }}
                gap={"150"}
                justifyContent={"space-between"}
              >
                <Button
                  flex={1}
                  size={"lg"}
                  type={"button"}
                  visual={"primary"}
                  mode={"light"}
                  {...cancelButtonProps}
                >
                  {cancelButtonCopy ?? t("core:CANCEL")}
                </Button>
                <Button flex={1} size={"lg"} {...saveButtonProps}>
                  {saveButtonCopy ?? t("core:MODAL_BUTTON_COPY.SAVE")}
                </Button>
              </Flex>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
